import * as Config from "config";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from 'react';
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";

const ComponentContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'nowrap',
  margin: theme.spacing(0, 3, 0, 3),
  width: '100%'
}));
const NameFields = styled('div')(() => ({
  alignItems: 'center',
  display: 'grid',
  gridTemplateColumns: '1fr min-content min-content',
  width: '100%'
}));

export type ProfileNameInputProps = {
  className?: string;
  name: string;
  draftName?: string;
  error?: boolean;
  isLoading?: boolean;
  onStartEditing?: () => void;
  onDraftChange?: (draft: string) => void;
  onCommit?: (finalName: string) => void;
  onCancel?: () => void;
}

export default function ProfileNameInput(props: Readonly<ProfileNameInputProps>) {
  const {
    className,
    error,
    isLoading,
    name,
    draftName,
    onStartEditing,
    onDraftChange,
    onCommit,
    onCancel
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  const [isEditingName, setIsEditingName] = useState<boolean>(false);

  useEffect(() => {
    if (
      name !== Config.defaultProfileName ||
      !isEditingName ||
      !inputRef.current
    ) return;

    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, [isEditingName, name]);

  const value = draftName ?? name;

  return (
    <ComponentContainer className={className}>
      {!isEditingName && (
        <Typography variant="h4" color={error ? 'error' : 'unset'}>
          {isLoading ? "Loading..." : props.name}
        </Typography>
      )}
      {isEditingName && (
        <NameFields>
          <TextField
            id="name"
            variant="outlined"
            slotProps={{ inputLabel: { shrink: Boolean(name) } }}
            label="Name"
            value={value}
            inputRef={inputRef}
            onChange={(event) => onDraftChange?.(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === KeyboardKey.Enter) {
                setIsEditingName(false);
                onCommit?.(value);
              }
              if (event.key === KeyboardKey.Escape) {
                setIsEditingName(false);
                onCancel?.();
              }
            }}
          />
          <Tooltip title="Accept Change">
            <span>
              <IconButton 
                onClick={() => {
                  setIsEditingName(false);
                  onCommit?.(value);
                }}
                color="success"
                disabled={
                  isLoading || 
                  value.trim().length === 0 || 
                  value === name
                }
                size="medium"
                aria-label='accept change'
              >
                <CheckIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel">
            <span>
              <IconButton 
                onClick={() => {
                  setIsEditingName(false);
                  onCancel?.();
                }}
                color="error"
                disabled={isLoading}
                aria-label="cancel"
                size="medium"
                edge="end"
              >
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        </NameFields>
      )}
      {!isEditingName && (
        <Tooltip title="Edit Name">
          <span>
            <IconButton
              style={{
                borderColor: 'primary'
              }}
              onClick={() => {
                onStartEditing?.();
                setIsEditingName(true);
              }}
              color="primary"
              disabled={isLoading}
              aria-label="edit name"
              size="medium"
              edge="end">
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </ComponentContainer>
  );
}
